import React, { useEffect } from 'react'
import styled from '@emotion/styled'
import RadioToggle from '../generic/toggles/radioToggle'
import { FlexDiv } from '../generic/Basics'
import { fontSizeXSmall, fontSize2XSmall, grayDark40, grayLight20 } from '../../constants/principles'
import MakeModel from './MakeModel'
import { ButtonGroupWrap } from '../generic/buttonGroup'
import Error from '../generic/Error'

const leadTypeQ = 'qrv/LeadType'
const lfNewQ = 'qrv/LookingForNewCars'
const lfCertifiedQ = 'qrv/LookingForCertifiedCars'
const lfUsedQ = 'qrv/LookingForUsedCars'
const voiVinQ = 'qrv/VoiVin'
const CAR_TYPES = [lfCertifiedQ, lfNewQ, lfUsedQ]

const PILLS_FOR_TYPE = {
  [leadTypeQ]: ['Yes', 'No'],
  [lfNewQ]: ['New Cars'],
  [lfCertifiedQ]: ['Certified Pre-Owned Cars'],
  [lfUsedQ]: ['Used Cars']
}

export default ({
  prospectDocuments,
  isDigitalRetail,
  cobrandingInfo,
  getQuestionResponseComment,
  saveQuestionResponse,
  getQuestionResponse,
  isError,
  contactErr,
  errors,
  setHasScrolled,
  scroll,
  isDesktop,
  acceptsRV,
  optOutType,
  isAdmin,
  isAssigned,
  dealerName,
  experienceState,
  saveMultipleQuestionRaw,
  updateGlobalState
}) => {
  const types = cobrandingInfo?.replacementVehicleTypes || CAR_TYPES
  const leadType = getQuestionResponse(leadTypeQ).v
  const { dealerIsDirectToKBB, dealerIsDirectToKBBv2 } = experienceState

  if (isDigitalRetail && !leadType) saveQuestionResponse(leadTypeQ, optOutType)
  if (!acceptsRV && !leadType) saveQuestionResponse(leadTypeQ, optOutType)
  if ((isAdmin || dealerIsDirectToKBB) && !leadType) saveQuestionResponse(leadTypeQ, optOutType)

  const targetElement = isError && !contactErr && errors && `#${errors[0] + 'Error'}`

  useEffect(() => {
    if (isError && targetElement) {
      scroll(targetElement)
      setHasScrolled(true)
    }
  })

  const showReplacementVehicleQuestions = leadType === 'OptIn' || !leadType

  const saveRvInfo = type => answer => {
    if (CAR_TYPES.includes(type) && answer === 0 && !leadType) {
      saveMultipleQuestionRaw([type, leadTypeQ, voiVinQ], [{ v: answer }, { v: 'OptIn' }, { c: undefined }])
    } else {
      saveMultipleQuestionRaw([type, voiVinQ], [{ v: answer }, { c: undefined }])
    }

    const text = PILLS_FOR_TYPE[type][0]
    updateGlobalState({ fireAnalytics: `voi_select_type:${text}/` })
  }

  const saveQuestionResponseLeadType = i => {
    saveMultipleQuestionRaw([leadTypeQ, voiVinQ], [{ v: i === 0 ? 'OptIn' : optOutType }, { c: undefined }])

    const text = PILLS_FOR_TYPE[leadTypeQ][i]
    updateGlobalState({ fireAnalytics: `voi_toggle:${text}/` })
  }

  return (
    ((!isDigitalRetail && acceptsRV) || leadType === 'OptIn') &&
    !(isAdmin || dealerIsDirectToKBB) && (
      <>
        <ContentDiv isDesktop={isDesktop}>
          <SubheaderDiv data-testid={`ci-plan-review-header`}>Are you planning to replace your car?</SubheaderDiv>
          <TextDiv data-testid={`ci-plan-review-desc`}>Your Offer will be the same either way.</TextDiv>
          <RadioToggle testId={`ci-replacement`} pills={PILLS_FOR_TYPE[leadTypeQ]} selectedIndex={leadType ? (leadType === 'OptIn' ? 0 : 1) : undefined} onClickItem={saveQuestionResponseLeadType} />
          {isError && errors.includes('leadType') && <Error isError={isError} id={'leadTypeError'} text={'Please tell us if you plan to replace your car.'} />}
        </ContentDiv>

        {showReplacementVehicleQuestions ? (
          <ContentDiv isDesktop={isDesktop}>
            <ReplacementDiv>
              <SubheaderDiv data-testid={`ci-replacement-header-text`}>What are you interested in next?</SubheaderDiv>
              <ToggleDiv>
                {CAR_TYPES.map(type => types.includes(type) && <RadioToggle key={type} testId="ci-replacement" pills={PILLS_FOR_TYPE[type]} selectedIndex={getQuestionResponse(type).v && 0} onClickItem={saveRvInfo(type)} uniformSize={false} />)}
              </ToggleDiv>
              {isError && errors.includes('lookingFor') && <Error isError={isError} id={'lookingForError'} text={'Please select at least one option.'} margin={'0 0 8px 0'} />}
              <MakeModelDiv>
                <MakeModel {...{ prospectDocuments, getQuestionResponseComment, saveMultipleQuestionRaw, updateGlobalState }} />
              </MakeModelDiv>
              {isError && errors.includes('ymm') && <Error isError={isError} id={'ymmError'} text={'Please select a year, make, and model.'} />}
            </ReplacementDiv>
          </ContentDiv>
        ) : dealerIsDirectToKBBv2 ? null : (
          <ContentDiv>
            <ReplacementDiv bold={true}>
              <NoReplaceDiv data-testid={`ci-txt-ready-for-cash-header`}>Great! {isAssigned && dealerName ? `${dealerName} is` : 'Participating Dealers are'} ready to buy your car for cash.</NoReplaceDiv>
            </ReplacementDiv>
          </ContentDiv>
        )}
      </>
    )
  )
}

const ContentDiv = styled(FlexDiv)`
  padding-top: 32px;
  padding-bottom: 32px;
  color: ${grayDark40};
  border-bottom: 1px solid ${grayLight20};
  width: ${p => (p.isDesktop ? 80 : 100)}%;
  :last-child {
    border-bottom: none;
  }
`
const MakeModelDiv = styled.div`
  margin-top: 16px;
`
const ToggleDiv = styled(ButtonGroupWrap)`
  padding: 16px 0 0 0;
  flex-direction: row;
`
const SubheaderDiv = styled.div`
  font-size: ${fontSizeXSmall};
  font-weight: bold;
  text-align: center;
`

const TextDiv = styled.div`
  padding-bottom: 24px;
  font-size: ${fontSize2XSmall};
`

const ReplacementDiv = styled(FlexDiv)`
  padding-bottom: 32px;
  ${p => p.bold && 'font-weight:bold;'}
  width: 100%;
`
const NoReplaceDiv = styled(FlexDiv)`
  flex-direction: row;
  width: 100%;
`
